import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="terms-page">
      <SEO path="/terms" />
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 gap-2">
          <ArrowLeft className="w-4 h-4" /> Terug
        </Button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-4xl md:text-5xl text-foreground mb-3">Algemene Voorwaarden</h1>
          <p className="text-muted-foreground mb-10">Laatst bijgewerkt: 19 februari 2026 · Versie 2.0</p>

          <div className="prose prose-lg max-w-none text-foreground space-y-8">
            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">1. Wie zijn wij?</h2>
              <div className="bg-muted/40 p-4 rounded-xl text-sm text-muted-foreground space-y-1">
                <p><strong className="text-foreground">Oma's Verhaaltjes</strong></p>
                <p>[INVULLEN: rechtsvorm en bedrijfsnaam, bv. Oma's Verhaaltjes B.V.]</p>
                <p>[INVULLEN: bezoekadres, postcode en plaats]</p>
                <p>KvK: [INVULLEN: KvK-nummer]</p>
                <p>BTW: [INVULLEN: BTW-identificatienummer]</p>
                <p>E-mail: <a className="text-gold hover:underline" href="mailto:info@omasverhaaltjes.nl">info@omasverhaaltjes.nl</a></p>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Oma's Verhaaltjes biedt een digitaal platform voor prentenboeken en bedtijdverhalen. Wij
                worden verder aangeduid als "Oma's Verhaaltjes", "wij", "ons" of "onze". De gebruiker
                wordt aangeduid als "je" of "jij".
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">2. Toepasselijkheid</h2>
              <p className="text-muted-foreground leading-relaxed">
                Deze voorwaarden zijn van toepassing op elk gebruik van onze website, web-app, mobiele
                app en alle aanverwante diensten. Door een account aan te maken of een abonnement af te
                sluiten, aanvaard je deze voorwaarden integraal.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">3. Accounts en leeftijd</h2>
              <p className="text-muted-foreground leading-relaxed">
                Om een account aan te maken moet je <strong>minimaal 16 jaar</strong> oud zijn. Voor
                abonnementen en cadeaubonnen geldt een minimumleeftijd van <strong>18 jaar</strong>. Je
                bent verantwoordelijk voor het geheim houden van je wachtwoord en voor alle activiteit
                onder je account. Neem bij misbruik direct contact op via <a className="text-gold hover:underline" href="mailto:info@omasverhaaltjes.nl">info@omasverhaaltjes.nl</a>.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">4. Abonnementen en prijzen</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Het premium-abonnement kost <strong>€2,99 per maand</strong>, inclusief 21% BTW. Prijzen worden weergegeven in euro's.</li>
                <li>Je krijgt eenmalig <strong>7 dagen gratis proefperiode</strong> wanneer je het abonnement start. Daarna wordt maandelijks automatisch verlengd.</li>
                <li>Betaling gebeurt via <strong>Mollie</strong> (iDEAL, SEPA-machtiging, creditcard).</li>
                <li>Je kunt <strong>op elk moment opzeggen</strong> via je account onder "Mijn abonnement". Na opzegging behoud je toegang tot het einde van de lopende betaalperiode.</li>
                <li>Cadeau-abonnementen zijn eenmalige betalingen en vernieuwen niet automatisch.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">5. Herroepingsrecht (EU)</h2>
              <p className="text-muted-foreground leading-relaxed">
                Als consument heb je op grond van Boek 6 BW artikel 230o een <strong>bedenktermijn van
                14 dagen</strong> na het sluiten van de overeenkomst om deze kosteloos te herroepen. Bij
                een digitale dienst vervalt dit recht echter zodra je vóór het verstrijken van de 14 dagen
                uitdrukkelijk hebt verzocht om directe toegang tot de content, en je hebt erkend dat je
                hiermee afstand doet van het herroepingsrecht (art. 6:230p sub g BW).
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Wij bieden daarom een <strong>7 dagen gratis proefperiode</strong> aan waarin je de dienst
                vrijblijvend kunt testen. Wil je alsnog je herroepingsrecht uitoefenen? Stuur een mail
                naar <a className="text-gold hover:underline" href="mailto:info@omasverhaaltjes.nl">info@omasverhaaltjes.nl</a> met onderwerp "Herroeping" en vermeld je account-e-mail.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">6. Content van gebruikers</h2>
              <p className="text-muted-foreground leading-relaxed">
                Als je verhalen of illustraties uploadt:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Je behoudt zelf het <strong>auteursrecht</strong> op je werk.</li>
                <li>Je verleent ons een wereldwijde, niet-exclusieve, royalty-vrije licentie om je werk op het platform te tonen, te vertalen en te reproduceren zolang het gepubliceerd is.</li>
                <li>Je garandeert dat je werk origineel is en geen rechten van derden schendt.</li>
                <li>Alle content wordt <strong>handmatig beoordeeld</strong> op kindvriendelijkheid vóór publicatie.</li>
                <li>Wij behouden ons het recht voor om content te weigeren, aan te passen of te verwijderen zonder opgaaf van reden.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Vermoed je een inbreuk op jouw rechten? Mail ons via <a className="text-gold hover:underline" href="mailto:info@omasverhaaltjes.nl">info@omasverhaaltjes.nl</a> (notice-and-takedown).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">7. Toegestaan gebruik</h2>
              <p className="text-muted-foreground leading-relaxed">Het is niet toegestaan om:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>content te downloaden, kopiëren of opnieuw te distribueren buiten het persoonlijke gebruik;</li>
                <li>de dienst te gebruiken voor commerciële doeleinden zonder onze schriftelijke toestemming;</li>
                <li>de beveiliging te omzeilen of anderen daartoe aan te zetten;</li>
                <li>schadelijke, beledigende of illegale content te uploaden;</li>
                <li>accounts te delen of meerdere accounts aan te maken om gratis verhalen te omzeilen.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">8. Beëindiging</h2>
              <p className="text-muted-foreground leading-relaxed">
                Je kunt je account op elk moment verwijderen via je profielinstellingen. Wij kunnen een
                account met onmiddellijke ingang opschorten of beëindigen bij ernstige schending van
                deze voorwaarden. Na beëindiging worden je persoonsgegevens verwijderd conform ons
                <a className="text-gold hover:underline" href="/privacy"> privacybeleid</a>, met uitzondering
                van wettelijk verplichte bewaartermijnen (bv. fiscale gegevens, 7 jaar).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">9. Aansprakelijkheid</h2>
              <p className="text-muted-foreground leading-relaxed">
                Wij spannen ons in voor een betrouwbare dienst, maar kunnen geen ononderbroken
                beschikbaarheid garanderen. Onze aansprakelijkheid voor indirecte schade is uitgesloten,
                behalve in geval van opzet of grove schuld. Onze totale aansprakelijkheid is beperkt tot
                het bedrag dat je in de <strong>laatste 12 maanden</strong> aan ons hebt betaald. Deze
                beperking geldt niet voor aansprakelijkheid die krachtens dwingend recht niet kan worden
                uitgesloten.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">10. Overmacht</h2>
              <p className="text-muted-foreground leading-relaxed">
                Onder overmacht verstaan wij onder meer: storingen bij internetproviders of
                betaalproviders, cyberaanvallen, brand, natuurrampen, overheidsmaatregelen en oorlog.
                Tijdens overmacht zijn onze verplichtingen opgeschort.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">11. Wijzigingen</h2>
              <p className="text-muted-foreground leading-relaxed">
                Wij mogen deze voorwaarden en prijzen wijzigen. Ingrijpende wijzigingen melden wij
                minimaal 30 dagen van tevoren per e-mail. Ben je het oneens met de wijziging? Dan kun je
                kosteloos opzeggen voordat de nieuwe voorwaarden ingaan.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">12. Toepasselijk recht</h2>
              <p className="text-muted-foreground leading-relaxed">
                Op deze voorwaarden is <strong>Nederlands recht</strong> van toepassing. Geschillen
                worden voorgelegd aan de bevoegde rechter te [INVULLEN: vestigingsplaats, bv. Amsterdam],
                behoudens dwingendrechtelijke bepalingen die een andere rechter aanwijzen (zoals het
                consumentenrecht in jouw woonplaats binnen de EU).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">13. Scheidbaarheid</h2>
              <p className="text-muted-foreground leading-relaxed">
                Is een bepaling van deze voorwaarden nietig of onafdwingbaar, dan blijven de overige
                bepalingen onverminderd van kracht. De nietige bepaling wordt vervangen door een bepaling
                die de oorspronkelijke bedoeling zo dicht mogelijk benadert.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">14. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vragen over deze voorwaarden? Mail ons via <a className="text-gold hover:underline" href="mailto:info@omasverhaaltjes.nl">info@omasverhaaltjes.nl</a>.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;
